import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productService, categoryService } from '../services/api';
import './ProductForm.css';
import { Typography, Row, Col, Card, Input, InputNumber, Select, Switch, Checkbox, Button, Alert, Upload, Space, Tag, Divider, Breadcrumb, Form, Popconfirm, Empty, App as AntdApp } from 'antd';
const { Title, Text } = Typography;
const { TextArea } = Input;

const CLEAN_DISPLAY_REPAIR_MAP = (() => {
  const FFD = '\uFFFD';
  const P = (pieces) => pieces.join(FFD);
  const R = (s, flags) => new RegExp(s, flags);
  const pairs = [
    [P(['D','tect']),       'Détect'],
    [P(['d','tect']),       'détect'],
    [P(['monophas']),       'monophasé'],
    [P(['triphas']),        'triphasé'],
    [P(['r','f','rence']),  'référence'],
    [P(['R','f','rence']),  'Référence'],
    [P(['d','crit']),       'décrit'],
    [P(['','quip']),        'équip'],
    [P(['','tabli']),       'établi'],
    [P(['','tabl']),        'établ'],
    [P(['s','curit']),      'sécurité'],
    [P(['S','curit']),      'Sécurité'],
    [P(['gr','ce']),        'grâce'],
    [P(['','nergie']),      'énergie'],
    [P(['','lectrique']),   'électrique'],
    [P(['','lectron']),     'électron'],
    [P(['fum','e']),        'fumée'],
    [P(['Fum','e']),        'Fumée'],
    [P(['fum','es']),       'fumées'],
    [P(['Fum','es']),       'Fumées'],
    [P(['qualit']),         'qualité'],
    [P(['autorit']),        'autorité'],
    [P(['optiqu','e']),     'optique'],
    [P(['Optiqu','e']),     'Optique'],
    [P(['ponctuel']),       'ponctuel'],
    [P(['Ponctuel']),       'Ponctuel'],
    [P(['Gamme']),          'Gamme'],
    [P(['Onduleur']),       'Onduleur'],
    [P(['onduleur']),       'onduleur'],
    [P(['Adressable']),     'Adressable'],
    [P(['adressable']),     'adressable'],
    [P(['parfaite']),       'parfaite'],
    [P(['','quip','s']),    'équipés'],
    [P(['','pur','']),      'épuré'],
    [P(['int','gration']),  'intégration'],
    [P(['d','part']),       'départ'],
    [P(['d','tecter']),     'détecter'],
    [P(['d','celer']),      'déceler'],
    [P(['pr','sentes']),    'présentes'],
    [P(['r','flexion']),    'réflexion'],
    [P(['particuli','rement']), 'particulièrement'],
    [P(['d','gagement']),   'dégagement'],
    [P(['compos','']),      'composé'],
    [P(['syst','me']),      'système'],
    [P(['g','n','ralement']),'généralement'],
    [P(['employ','']),      'employé'],
    [P(['','tablissements']),'établissements'],
    [P(['galement']),       'également'],
    [P(['id','al']),        'idéal'],
    [P(['d','tection']),    'détection'],
    [P(['combustion']),     'combustion']
  ];
  return pairs.map(([p, r]) => [R(p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), r]);
})();

const cleanDisplay = (v) => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  if (Array.isArray(v)) return v.map(x => cleanDisplay(x));
  if (typeof v === 'object') {
    const out = {};
    for (const k of Object.keys(v)) out[k] = cleanDisplay(v[k]);
    return out;
  }
  let s = String(v);
  if (/\uFFFD/.test(s)) {
    for (const [rx, replacement] of CLEAN_DISPLAY_REPAIR_MAP) {
      s = s.replace(rx, replacement);
    }
  }
  s = s.replace(/\uFFFD{2,}/g, ' · ');
  s = s.replace(/\uFFFD/g, '');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
};

/* ============================================================
   ARBRE MÉTIER PAR DÉFAUT (10 organigrammes officiels) — Utilisé comme FALLBACK
   si AdminFilters n'a pas d'arbre custom enregistré (clé domoelec:facetTemplates.familyTree).
   ============================================================ */
const DEFAULT_CATEGORY_TREE_STATIC = [
  {
    name: 'ÉCLAIRAGE DE SÉCURITÉ',
    children: [
      { name: 'ÉCLAIRAGE AUTONOME', children: [{ name: "BLOC D'AMBIANCE" }, { name: "BLOC D'ÉVACUATION" }, { name: 'ACCESSOIRES' }] },
      {
        name: 'ÉCLAIRAGE SUR SOURCE',
        children: [
          { name: 'ADRESSABLE', children: [{ name: 'SOURCE CENTRALE' }, { name: 'LUMINAIRE' }] },
          { name: 'CONVENTIONNEL', children: [{ name: 'SOURCE CENTRALE' }, { name: "LUMINAIRE D'ÉCLAIRAGE" }] }
        ]
      }
    ]
  },
  {
    name: 'ONDULEUR',
    children: [
      { name: 'PHASE', children: [{ name: 'THREE-PHASE' }, { name: 'SINGLE-PHASE' }] },
      { name: 'PUISSANCE', children: [{ name: '0-5 KVA' }, { name: '6-15 KVA' }, { name: '20-100' }] }
    ]
  },
  {
    name: 'DÉTECTION INCENDIE',
    children: [
      { name: 'MARQUE', children: [{ name: 'NUGELEC' }, { name: 'COOPER' }] },
      { name: 'NORME', children: [{ name: 'NF' }, { name: 'EN' }] },
      { name: 'SOLUTION', children: [{ name: 'CONVENTIONNEL' }, { name: 'ADRESSABLE' }] },
      { name: 'ÉQUIPEMENTS', children: [{ name: 'CENTRALE' }, { name: 'AES' }, { name: 'PÉRIPHÉRIQUE' }, { name: 'SIRÈNES' }, { name: 'AUTRES' }] }
    ]
  },
  {
    name: "CONTRÔLE D'ACCÈS & POINTAGE",
    children: [
      { name: 'MARQUE', children: [{ name: 'HIKVISION' }, { name: 'ZKTECO' }] },
      {
        name: 'CATÉGORIE',
        children: [
          { name: "CONTRÔLE D'ACCÈS", children: [{ name: 'CONTRÔLEUR' }, { name: 'TERMINAL' }] },
          { name: 'GESTION DE TEMPS & POINTAGE', children: [{ name: 'POINTEUSE' }, { name: 'LOGICIEL' }] },
          { name: "CONTRÔLE D'ENTRÉE", children: [{ name: 'TOURNIQUET' }, { name: 'BARRIÈRE DE PARKING' }] },
          { name: "ACCESSOIRE D'ACCÈS" }
        ]
      }
    ]
  },
  {
    name: 'VIDÉOSURVEILLANCE',
    children: [
      { name: 'MARQUE', children: [{ name: 'HIKVISION' }] },
      { name: 'CATÉGORIE', children: [{ name: 'CAMÉRA IP' }, { name: 'CAMÉRA PTZ' }, { name: 'ENREGISTREURS VIDÉO RÉSEAU NVR' }] }
    ]
  },
  {
    name: 'INTRUSION',
    children: [
      { name: 'MARQUE', children: [{ name: 'TECNOALARME' }, { name: 'HIKVISION' }] },
      { name: 'CONNEXION', children: [{ name: 'FILAIRE' }, { name: 'SANS FIL' }] },
      { name: 'CATÉGORIE', children: [{ name: 'CENTRALE INTRUSION' }, { name: 'DÉTECTEURS' }, { name: 'SIRÈNES' }, { name: 'ACCESSOIRES' }] }
    ]
  },
  {
    name: 'DOMOTIQUE / GRMS',
    children: [
      { name: 'MARQUE', children: [{ name: 'ZENNIO' }, { name: 'HDL' }, { name: 'COMELIT' }] },
      { name: 'SOLUTION', children: [{ name: 'KNX' }, { name: 'BUS' }, { name: 'SANS FILS' }, { name: 'AUTRES' }] },
      { name: 'CATÉGORIE', children: [{ name: 'CAPTEURS' }, { name: 'INTERFACES AUDIO/VIDÉO' }, { name: 'ACTIONNEURS' }, { name: 'CONTRÔLEURS' }, { name: 'PASSERELLES' }] },
      { name: 'FONCTIONNALITÉ', children: [{ name: "GESTION DE L'ÉCLAIRAGE" }, { name: 'CONTRÔLE DE LA CLIMATISATION' }, { name: 'AUTOMATISATION DE VOLETS' }, { name: 'INTÉGRATION AUDIO/VIDÉO' }] }
    ]
  },
  {
    name: 'SONORISATION',
    children: [
      { name: 'MARQUE', children: [{ name: 'BOSE' }, { name: 'DSPPA' }] },
      { name: 'CATÉGORIE', children: [{ name: 'ENCEINTES PASSIVES' }, { name: 'ENCEINTES ACTIVES' }, { name: 'AMPLIFICATEURS' }, { name: 'ACCESSOIRES AUDIO' }] },
      { name: 'USAGE', children: [{ name: 'INTÉRIEUR' }, { name: 'EXTÉRIEUR' }] },
      { name: 'CONNECTIVITÉ', children: [{ name: 'FILAIRE' }, { name: 'SANS FIL' }] }
    ]
  },
  {
    name: 'EXTINCTION',
    children: [
      { name: 'MARQUE', children: [{ name: 'AGUILERA' }] },
      { name: 'TYPE DE GAZ', children: [{ name: 'FE-13' }, { name: 'HFC-227EA' }, { name: 'NOVEC TM' }, { name: 'CO2' }] },
      { name: 'CATÉGORIE', children: [{ name: 'CENTRALE' }, { name: 'DÉTECTEURS' }, { name: 'DÉCLENCHEURS' }, { name: 'SIRÈNES' }] }
    ]
  },
  {
    name: 'DÉTECTION DE GAZ',
    children: [
      { name: 'MARQUE', children: [{ name: 'SENSITRON' }] },
      { name: 'TYPE DE GAZ', children: [{ name: 'GAZ INFLAMMABLES' }, { name: 'GAZ TOXIQUES' }, { name: 'MULTI GAZ' }] },
      { name: 'TYPE DE DÉTECTEURS', children: [{ name: 'DÉTECTEUR FIXE' }, { name: 'DÉTECTEUR PORTABLE' }, { name: 'DÉTECTEURS POUR PARKING' }] }
    ]
  }
];

const normalizeName = value =>
  String(value || '')
    .trim().toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[’'`]/g, "'").replace(/\s+/g, ' ');

const GROUP_TO_FACET_KEY = {
  'MARQUE': 'brand', 'BRAND': 'brand',
  'PHASE': 'phase', 'PUISSANCE': 'puissance', 'POWER': 'puissance',
  'NORME': 'norme', 'STANDARD': 'norme',
  'SOLUTION': 'solution', 'SYSTEME': 'system', 'SYSTÈME': 'system', 'SYSTEM': 'system',
  'EQUIPEMENT': 'equipement', 'EQUIPEMENTS': 'equipement', 'ÉQUIPEMENT': 'equipement', 'ÉQUIPEMENTS': 'equipement',
  'TYPE DE GAZ': 'typeGaz', 'TYPEGAZ': 'typeGaz',
  'TYPE DE DÉTECTEUR': 'typeDetecteur', 'TYPE DE DÉTECTEURS': 'typeDetecteur', 'TYPE DE DETECTEURS': 'typeDetecteur',
  'USAGE': 'usage', 'UTILISATION': 'usage',
  'CONNEXION': 'connectivity', 'CONNECTIVITE': 'connectivity', 'CONNECTIVITÉ': 'connectivity', 'CONNECTIVITY': 'connectivity', 'CONNECTION': 'connectivity',
  'TECHNOLOGIE': 'technologie', 'TECHNOLOGY': 'technologie',
  'RESOLUTION': 'resolution', 'RÉSOLUTION': 'resolution',
  'DEBIT': 'debit', 'DÉBIT': 'debit', 'FLOW': 'debit',
  'FONCTIONNALITE': 'fonctionnalite', 'FONCTIONNALITÉ': 'fonctionnalite', 'FUNCTIONALITY': 'fonctionnalite', 'FONCTIONNALITES': 'fonctionnalite',
  'CATEGORIE': 'categorie', 'CATÉGORIE': 'categorie', 'CATEGORY': 'categorie', 'TYPE': 'type'
};

const FACET_LABEL_FR = {
  brand: 'Marque', system: 'Système', typeGaz: 'Type de gaz',
  typeDetecteur: 'Type de détecteur', detectorType: 'Type de détecteur',
  usage: 'Usage', connectivity: 'Connectivité',
  phase: 'Phase', puissance: 'Puissance', norme: 'Norme',
  solution: 'Solution', equipement: 'Équipement', categorie: 'Catégorie',
  type: 'Type', resolution: 'Résolution', technologie: 'Technologie',
  debit: 'Débit', fonctionnalite: 'Fonctionnalité'
};

const FACET_ADMIN_DEFS = [
  { k: 'brand',          label: 'Marque',              isMultiple: false, icon: '🏷️' },
  { k: 'power',          label: 'Puissance (W/kVA)',   isMultiple: false, icon: '⚡' },
  { k: 'phase',          label: 'Phase',               isMultiple: false, icon: '🔌' },
  { k: 'voltage',        label: 'Tension (V)',         isMultiple: false, icon: '🔋' },
  { k: 'frequency',      label: 'Fréquence (Hz)',      isMultiple: false, icon: '📊' },
  { k: 'technology',     label: 'Technologie',         isMultiple: false, icon: '🧬' },
  { k: 'certification',  label: 'Certification/Norme', isMultiple: true,  icon: '✅' },
  { k: 'protection',     label: 'Indice Protection',   isMultiple: false, icon: '🛡️' },
  { k: 'warranty',       label: 'Garantie (années)',   isMultiple: false, icon: '📆' },
  { k: 'material',       label: 'Matériau/Carcasse',   isMultiple: false, icon: '🧱' },
  { k: 'color',          label: 'Couleur',             isMultiple: false, icon: '🎨' },
  { k: 'mounting',       label: 'Montage/Installation',isMultiple: false, icon: '🔧' },
  { k: 'efficiency',     label: 'Rendement (%)',       isMultiple: false, icon: '📈' },
  { k: 'batteryTech',    label: 'Batterie (Technologie)', isMultiple: false, icon: '🔋' },
  { k: 'inverterType',   label: 'Type Onduleur',       isMultiple: false, icon: '🔁' },
  { k: 'connector',      label: 'Connecteur/Sorties',  isMultiple: true,  icon: '🔗' },
  { k: 'origin',         label: 'Origine/Fabricant',   isMultiple: false, icon: '🌍' },
];
const FACET_ADMIN_BY_KEY = FACET_ADMIN_DEFS.reduce((acc, f) => { acc[f.k] = f; return acc; }, {});

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { message: appMsg } = AntdApp.useApp();

  const [formData, setFormData] = useState({
    name: '', reference: '', description: '', shortDescription: '',
    categories: [], status: 'draft', isVisible: false, isFeatured: false,
    features: [], technicalSpecs: {}, facets: {},
    metaTitle: '', metaDescription: '', seoKeywords: []
  });

  const [categories, setCategories] = useState([]);
  const [cascadePath, setCascadePath] = useState([]);
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState({ datasheet: null, manual: null, certificate: null });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(isEdit);
  const [error, setError] = useState(null);
  const [familyTreeVersion, setFamilyTreeVersion] = useState(0);
  const [facetTemplatesVersion, setFacetTemplatesVersion] = useState(0);
  const [backendCategoryVersion, setBackendCategoryVersion] = useState(0);

  const [dynBranchSelections, setDynBranchSelections] = useState({});
  const [dynFamilyId, setDynFamilyId] = useState(null);
  const [dynBranches, setDynBranches] = useState([]);
  const [dynLoading, setDynLoading] = useState(false);

  const [dynAttrRows, setDynAttrRows] = useState([{ criterion: '', value: '' }]);
  const addDynAttrRow = () => setDynAttrRows(prev => [...prev, { criterion: '', value: '' }]);
  const removeDynAttrRow = idx => setDynAttrRows(prev => prev.filter((_, i) => i !== idx));
  const updateDynAttrRow = (idx, key, val) => setDynAttrRows(prev => prev.map((r, i) => i === idx ? { ...r, [key]: val } : r));

  // ===== NOUVEAU : Facettes Famille→Critères→Valeurs (affichage SÉPARÉ, sans mélange) =====
  const [facFamilleTree, setFacFamilleTree] = useState([]);
  const [facFamilleLoading, setFacFamilleLoading] = useState(true);
  const facFamilyOptions = useMemo(() => {
    if (!Array.isArray(facFamilleTree)) return [];
    return facFamilleTree
      .filter(f => f && typeof f.famille === 'string' && f.famille.trim())
      .sort((a, b) => String(a.famille).localeCompare(String(b.famille), 'fr'));
  }, [facFamilleTree]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setFacFamilleLoading(true);
        const fres = await productService.getProductFacets({});
        if (alive) setFacFamilleTree(Array.isArray(fres?.familyTree) ? fres.familyTree : []);
      } catch (_) { if (alive) setFacFamilleTree([]); }
      finally { if (alive) setFacFamilleLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const [iaGenerating, setIaGenerating] = useState(false);
  const [descPreviewMode, setDescPreviewMode] = useState(true);

  const FACET_DEFS = [
    { k: 'brand',          label: 'Marque',              isMultiple: false, icon: '🏷️' },
    { k: 'power',          label: 'Puissance (W/kVA)',   isMultiple: false, icon: '⚡' },
    { k: 'phase',          label: 'Phase',               isMultiple: false, icon: '🔌' },
    { k: 'voltage',        label: 'Tension (V)',         isMultiple: false, icon: '🔋' },
    { k: 'frequency',      label: 'Fréquence (Hz)',      isMultiple: false, icon: '📊' },
    { k: 'technology',     label: 'Technologie',         isMultiple: false, icon: '🧬' },
    { k: 'certification',  label: 'Certification/Norme', isMultiple: true,  icon: '✅' },
    { k: 'protection',     label: 'Indice Protection',   isMultiple: false, icon: '🛡️' },
    { k: 'warranty',       label: 'Garantie (années)',   isMultiple: false, icon: '📆' },
    { k: 'material',       label: 'Matériau/Carcasse',   isMultiple: false, icon: '🧱' },
    { k: 'color',          label: 'Couleur',             isMultiple: false, icon: '🎨' },
    { k: 'mounting',       label: 'Montage/Installation',isMultiple: false, icon: '🔧' },
    { k: 'efficiency',     label: 'Rendement (%)',       isMultiple: false, icon: '📈' },
    { k: 'batteryTech',    label: 'Batterie (Technologie)', isMultiple: false, icon: '🔋' },
    { k: 'inverterType',   label: 'Type Onduleur',       isMultiple: false, icon: '🔁' },
    { k: 'connector',      label: 'Connecteur/Sorties',  isMultiple: true,  icon: '🔗' },
    { k: 'origin',         label: 'Origine/Fabricant',   isMultiple: false, icon: '🌍' },
  ];
  const FACET_BY_KEY = FACET_DEFS.reduce((acc, f) => { acc[f.k] = f; return acc; }, {});

  // --- Helper de lecture partagée multi-supports (contourne Tracking Prevention)
  //     Ordre priorité : RAM globale window.__domoelecFiltersStore → sessionStorage → localStorage
  const readSharedFilters = () => {
    try {
      if (window.__domoelecFiltersStore && typeof window.__domoelecFiltersStore === 'object') {
        return window.__domoelecFiltersStore;
      }
      let raw = null;
      try { raw = sessionStorage.getItem('domoelec:facetTemplates'); } catch { raw = null; }
      if (raw) return JSON.parse(raw) || null;
      try { raw = localStorage.getItem('domoelec:facetTemplates'); } catch { raw = null; }
      if (raw) return JSON.parse(raw) || null;
      return null;
    } catch { return null; }
  };

  // 🔁 Chargement DYNAMIQUE de l'arbre métier (10 FAMILLES + ENFANTS) depuis AdminFilters
  //    Clé : domoelec:facetTemplates.familyTree (fallback = DEFAULT_CATEGORY_TREE_STATIC)
  //    — Recharge toutes les 1.2s + CustomEvent + BroadcastChannel + focus.
  //    Plus jamais de dépendance à localStorage seul (Tracking Prevention ne bloque plus)
  const loadFamilyTree = () => {
    const p = readSharedFilters();
    if (p && Array.isArray(p.familyTree) && p.familyTree.length > 0) return p.familyTree;
    return DEFAULT_CATEGORY_TREE_STATIC;
  };

  useEffect(() => {
    const bump = () => { setFamilyTreeVersion(v => v + 1); setFacetTemplatesVersion(v => v + 1); };
    const bumpFilters = () => setFacetTemplatesVersion(v => v + 1);
    const iv = setInterval(bump, 1200);
    window.addEventListener('storage', bump);
    window.addEventListener('domoelec:filters-updated', bumpFilters);
    window.addEventListener('focus', bumpFilters);
    // BroadcastChannel (cross-tab synchro — fonctionne SANS storage autorisé)
    let bc = null;
    try { bc = new BroadcastChannel('domoelec-filters-channel'); }
    catch { bc = null; }
    if (bc) {
      const onBcMsg = e => {
        if (e?.data?.type === 'filters-updated') {
          // Mettre à jour RAM locale aussi
          try { if (e.data.payload) window.__domoelecFiltersStore = e.data.payload; } catch { /* ignore */ }
          bump();
        }
      };
      bc.onmessage = onBcMsg;
      try { bc.onmessage = onBcMsg; } catch { /* ignore */ }
    }
    return () => {
      clearInterval(iv);
      window.removeEventListener('storage', bump);
      window.removeEventListener('domoelec:filters-updated', bumpFilters);
      window.removeEventListener('focus', bumpFilters);
      try { if (bc) bc.close && bc.close(); } catch { /* ignore */ }
    };
  }, []);

  const CATEGORY_TREE = useMemo(loadFamilyTree, [familyTreeVersion, cascadePath.length, categories.length, isEdit]);

  // 🔁 MERGED CATEGORY TREE (source PRIORITÉ 1)
  //    — Règle: backend categories (rafraîchi polling 1.5s) > storage legacy
  //    — Fusion récursive par nom normalisé. Ajoute toutes NOUVELLES familles backend
  //    + préserve familles storage si backend vide.
  const buildBackendCategoryTree = () => {
    if (!Array.isArray(categories) || categories.length === 0) return [];
    const byId = new Map();
    categories.forEach(c => { byId.set(String(c.id), { ...c, children: [] }); });
    const roots = [];
    byId.forEach(node => {
      const pid = node.parentCategoryId || node.parentCategory?.id;
      if (pid != null && byId.has(String(pid))) {
        byId.get(String(pid)).children.push(node);
      } else roots.push(node);
    });
    const sortFn = (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
    const walk = nodes => {
      nodes.sort(sortFn);
      nodes.forEach(n => { if (Array.isArray(n.children) && n.children.length) walk(n.children); });
    };
    walk(roots);
    return roots;
  };
  const BACKEND_TREE = useMemo(buildBackendCategoryTree, [categories, backendCategoryVersion]);

  const mergeCategoryTreesRecursive = (backendNodes = [], legacyNodes = []) => {
    const outByName = new Map();
    backendNodes.forEach(bn => {
      const key = normalizeName(bn.name || '');
      outByName.set(key, {
        name: bn.name || (bn.slug ? String(bn.slug).replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : `#${bn.id || 'x'}`),
        id: bn.id,
        children: [],
        _isBackend: true
      });
    });
    legacyNodes.forEach(ln => {
      const key = normalizeName(ln.name || '');
      if (!key) return;
      const existing = outByName.get(key);
      if (existing) {
        existing.children = mergeCategoryTreesRecursive(existing.children || [], ln.children || []);
        if (existing.id == null && ln.id != null) existing.id = ln.id;
      } else {
        outByName.set(key, { name: ln.name, id: ln.id, children: Array.isArray(ln.children) ? mergeCategoryTreesRecursive([], ln.children) : [], _isBackend: false });
      }
    });
    return Array.from(outByName.values());
  };
  const CATEGORY_TREE_MERGED = useMemo(() => mergeCategoryTreesRecursive(BACKEND_TREE, CATEGORY_TREE), [BACKEND_TREE, CATEGORY_TREE]);

  // 🔁 Chargement templates FACETTES (valeurs Marque, Puissance, etc.) depuis AdminFilters
  const loadTemplates = () => {
    const p = readSharedFilters();
    if (p && p.templates && typeof p.templates === 'object') return p.templates;
    return {};
  };
  const sharedTemplates = useMemo(loadTemplates, [facetTemplatesVersion]);

  const normAdmin = v => String(v || '').trim().toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ');

  // Flatten organigramme AdminFilters pour sélecteur 1-clic
  const flattenAdminTree = (nodes = CATEGORY_TREE, prefix = '', out = []) => {
    nodes.forEach(n => {
      const label = prefix ? `${prefix} > ${n.name}` : String(n.name);
      out.push({ value: label, label, nodePath: label.split(' > ') });
      if (Array.isArray(n.children) && n.children.length > 0) {
        flattenAdminTree(n.children, label, out);
      }
    });
    return out;
  };

  /* ============================================================
     HELPERS BACKEND (catégories réelles)
     ============================================================ */
  const getCategoryById = cid => (cid == null ? null : categories.find(c => String(c.id) === String(cid)) || null);

  const getRawChildren = pid => {
    if (pid == null) return [];
    return categories.filter(c => {
      const p = c.parentCategoryId || c.parentCategory?.id;
      return p != null && String(p) === String(pid);
    });
  };

  const getRawDescendants = (pid, maxDepth = 5) => {
    const result = [];
    const queue = getRawChildren(pid).map(item => ({ item, depth: 1 }));
    const visited = new Set();
    while (queue.length > 0) {
      const { item, depth } = queue.shift();
      if (!item || visited.has(String(item.id))) continue;
      visited.add(String(item.id));
      result.push(item);
      if (depth < maxDepth) getRawChildren(item.id).forEach(child => queue.push({ item: child, depth: depth + 1 }));
    }
    return result;
  };

  const resolveBackendCategory = (nodeName, parentBackendId = null) => {
    const wanted = normalizeName(nodeName);
    if (parentBackendId != null) {
      const direct = getRawChildren(parentBackendId).find(c => normalizeName(c.name) === wanted);
      if (direct) return direct;
      const desc = getRawDescendants(parentBackendId).find(c => normalizeName(c.name) === wanted);
      if (desc) return desc;
    }
    const roots = categories.filter(c => !(c.parentCategoryId || c.parentCategory?.id));
    const rm = roots.find(c => normalizeName(c.name) === wanted);
    if (rm) return rm;
    return categories.find(c => normalizeName(c.name) === wanted) || null;
  };

  /* ============================================================
     HELPERS ARBRE MÉTIER (utilise le TREE MERGÉ — backend prioritaire)
     ============================================================ */
  const getBusinessNodeAtPath = path => {
    if (!Array.isArray(path) || path.length === 0) return null;
    let nodes = CATEGORY_TREE_MERGED;
    let current = null;
    for (const pn of path) {
      current = nodes.find(n => normalizeName(n.name) === normalizeName(pn));
      if (!current) return null;
      nodes = current.children || [];
    }
    return current;
  };

  const getBusinessChildren = path => {
    if (!Array.isArray(path) || path.length === 0) return CATEGORY_TREE_MERGED;
    const node = getBusinessNodeAtPath(path);
    return node?.children || [];
  };

  // 🌟 LABEL DYNAMIQUE du N-ième Select
  // RÈGLE IMPÉRATIVE : idx≥1 => le titre est LE NOM DU PARENT DONT on liste les enfants
  // (la valeur affichée DANS le select N'EST PAS le titre du select !)
  const getLinearLevelLabel = (idx, path) => {
    const step = idx + 1;
    if (idx === 0) return `${step}. FAMILLE`;
    // On veut le NOM DU PARENT (dont on va afficher les enfants)
    // Pour le select positionné à idx, les valeurs qu'il propose = ENFANTS de cascadePath[idx-1]
    const parentName = path[idx - 1];
    if (parentName) return `${step}. ${String(parentName).toUpperCase()}`;
    return `${step}. SOUS-CATÉGORIE`;
  };

  const selectedRootName = cascadePath.length > 0 ? cascadePath[0] : null;
  const selectedBusinessNode = cascadePath.length > 0 ? getBusinessNodeAtPath(cascadePath) : null;
  const nextBusinessOptions = selectedRootName ? getBusinessChildren(cascadePath) : [];
  const isFinalSelection = Boolean(selectedBusinessNode) && (!selectedBusinessNode.children || selectedBusinessNode.children.length === 0);

  const resolvedBackendPath = useMemo(() => {
    if (cascadePath.length === 0) return [];
    const resolved = []; let parentBackendId = null;
    for (const nodeName of cascadePath) {
      const bc = resolveBackendCategory(nodeName, parentBackendId);
      if (!bc) { resolved.push(null); parentBackendId = null; }
      else { resolved.push(bc); parentBackendId = bc.id; }
    }
    return resolved;
  }, [cascadePath, categories]);

  // 🟡 FALLBACK SI FEUILLE CUSTOM N'EXISTE PAS EN BDD
  //    - Cas fréquent : admin a créé "test → testt → tde" DANS AdminFilters
  //      mais OUBLIÉ de créer la catégorie correspondante DANS backend table categories.
  //    - Comportement : On prend LE DERNIER NŒUD RÉSOLU (le plus profond résolu en base)
  //      et on ajoute le chemin complet dans categoryPathText (pour le retrouver côté liste produit).
  //    - Avertissement Jaune NON-BLOQUANT au lieu d'Erreur rouge bloquante.
  const finalBackendCategory = (isFinalSelection && resolvedBackendPath.length > 0)
    ? resolvedBackendPath[resolvedBackendPath.length - 1] : null;
  const lastResolvedBackend = resolvedBackendPath.reduceRight((acc, cur) => acc || cur, null);
  const fallbackUsed = isFinalSelection && cascadePath.length > 0 && !finalBackendCategory && Boolean(lastResolvedBackend);

  const effectiveCategory = finalBackendCategory || (fallbackUsed ? lastResolvedBackend : null);

  const unresolvedSelectedNames = cascadePath.filter((_, i) => !resolvedBackendPath[i]);
  const cascadePathObjects = useMemo(() => resolvedBackendPath.filter(Boolean), [resolvedBackendPath]);

  /* ============================================================
     CHANGEMENTS CASCADE
     ============================================================ */
  const syncFinalCategory = newPath => {
    const node = getBusinessNodeAtPath(newPath);
    const final = Boolean(node) && (!node.children || node.children.length === 0);
    if (!final) {
      setFormData(prev => ({ ...prev, categories: [] }));
      return;
    }
    let parentBackendId = null; let finalBackend = null;
    for (const nodeName of newPath) {
      finalBackend = resolveBackendCategory(nodeName, parentBackendId);
      if (!finalBackend) break;
      parentBackendId = finalBackend.id;
    }
    setFormData(prev => ({ ...prev, categories: finalBackend ? [finalBackend.id] : [] }));
  };

  const selectFamily = familyName => {
    if (!familyName) { setCascadePath([]); setFormData(prev => ({ ...prev, categories: [] })); return; }
    const newPath = [familyName]; setCascadePath(newPath); syncFinalCategory(newPath);
  };

  const selectCascadeLevel = (levelIndex, nodeName) => {
    const newPath = cascadePath.slice(0, levelIndex);
    if (nodeName) newPath.push(nodeName);
    setCascadePath(newPath); syncFinalCategory(newPath);
  };

  /* ============================================================
     MODE ÉDITION : retrouver chemin métier depuis un ID backend
     ============================================================ */
  const flattenBusinessPaths = (nodes, prefix = []) => {
    const paths = [];
    nodes.forEach(node => {
      const currentPath = [...prefix, node.name];
      paths.push(currentPath);
      if (node.children?.length) paths.push(...flattenBusinessPaths(node.children, currentPath));
    });
    return paths;
  };
  const allBusinessPaths = useMemo(() => flattenBusinessPaths(CATEGORY_TREE_MERGED), [CATEGORY_TREE_MERGED]);

  const findBusinessPathFromBackendId = backendId => {
    if (!backendId) return [];
    for (const path of allBusinessPaths) {
      let parentBackendId = null; let lastResolved = null; let valid = true;
      for (const nodeName of path) {
        lastResolved = resolveBackendCategory(nodeName, parentBackendId);
        if (!lastResolved) { valid = false; break; }
        parentBackendId = lastResolved.id;
      }
      if (valid && lastResolved && String(lastResolved.id) === String(backendId)) return path;
    }
    return [];
  };

  // Facettes auto-dérivées depuis le parcours cascade
  useEffect(() => {
    if (cascadePath.length < 2) return;
    const newFacets = { ...(formData.facets || {}) };
    let modified = false;
    for (let i = 0; i < cascadePath.length - 1; i++) {
      const parentName = cascadePath[i];
      const valueName = cascadePath[i + 1];
      const groupCat = resolveBackendCategory(parentName, resolvedBackendPath[i - 1]?.id || null);
      if (!groupCat) continue;
      const rawName = groupCat.name || '';
      const upper = String(rawName).toUpperCase().trim();
      const upperNoAccent = upper.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9]/g, '');
      let fk = GROUP_TO_FACET_KEY[upper] || GROUP_TO_FACET_KEY[upperNoAccent] || null;
      if (!fk) continue;
      if (newFacets[fk] !== valueName) { modified = true; newFacets[fk] = valueName; }
    }
    if (modified) setFormData(prev => ({ ...prev, facets: newFacets }));
  }, [cascadePath]);

  /* ============================================================
     CHAMPS SPÉCIFIQUES & FACETTES DYNAMIQUES
     ============================================================ */
  const getCategorySpecificFields = rootName => {
    if (!rootName) return [];
    const rootBackend = resolveBackendCategory(rootName);
    if (!rootBackend?.specificFields) return [];
    let sf = rootBackend.specificFields;
    if (typeof sf === 'string') { try { sf = JSON.parse(sf); } catch { sf = {}; } }
    if (!sf || typeof sf !== 'object') return [];
    return Object.entries(sf)
      .filter(([, cfg]) => cfg && typeof cfg === 'object')
      .map(([key, cfg]) => ({
        key, label: cfg.label || key, type: cfg.type || 'text',
        options: Array.isArray(cfg.options) ? cfg.options : [],
        required: cfg.required || false, isMultiple: cfg.type === 'multiselect'
      }));
  };
  const specificFields = getCategorySpecificFields(selectedRootName);

  const getFacetKey = catOrName => {
    if (!catOrName) return null;
    const rawName = typeof catOrName === 'object' ? catOrName.name || '' : String(catOrName);
    const upper = String(rawName).toUpperCase().trim();
    const upperNoAccent = upper.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^A-Z0-9]/g, '');
    let fk = GROUP_TO_FACET_KEY[upper] || GROUP_TO_FACET_KEY[upperNoAccent] || null;
    if (!fk) {
      fk = String(rawName).toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    }
    return fk;
  };

  const getAllLeaves = (cid, visited = null) => {
    if (!cid) return [];
    const v = visited || new Set();
    if (v.has(String(cid))) return [];
    v.add(String(cid));
    const cat = typeof cid === 'object' ? cid : getCategoryById(cid);
    if (!cat) return [];
    const kids = getRawChildren(cat.id);
    if (!kids.length) return [cat];
    const out = []; kids.forEach(k => out.push(...getAllLeaves(k, v)));
    return out;
  };

  const hasChildren = cid => getRawChildren(cid).length > 0;

  // 🌟 Facettes prédéfinies : 1) AdminFilters (modèles) > 2) CATEGORIE_TREE dynamic
  //  — Rechargées AUTOMATIQUEMENT dès qu'AdminFilters modifie, via facetTemplatesVersion (interval 1.2s + CustomEvent + BroadcastChannel + storage + focus)
  const facetTemplates = useMemo(() => {
    const p = readSharedFilters();
    if (!p) return {};
    return p.templates && typeof p.templates === 'object' ? p.templates : {};
  }, [facetTemplatesVersion]);

  // --- Helpers pour lire l'arbre famille STORAGE (CATEGORY_TREE) comme source de dynamic fields aussi ---
  const findRootInCategoryTree = rootName => {
    if (!Array.isArray(CATEGORY_TREE) || !rootName) return null;
    const n = String(rootName).trim().toLowerCase();
    return CATEGORY_TREE.find(x => String(x.name || '').trim().toLowerCase() === n) || null;
  };
  const collectTreeLeaves = node => {
    if (!node) return [];
    const names = [];
    const walk = (n) => {
      if (!n) return;
      const kids = Array.isArray(n.children) ? n.children : [];
      if (kids.length === 0) { if (n.name) names.push(n.name); return; }
      kids.forEach(walk);
    };
    (node.children || []).forEach(c => walk(c));
    return names;
  };
  const treeChildHasNested = childNode => {
    if (!childNode || !Array.isArray(childNode.children)) return false;
    return childNode.children.some(gc => Array.isArray(gc.children) && gc.children.length > 0);
  };

  const getDynamicFacetFields = rootName => {
    if (!rootName) return [];
    const fields = [];
    const added = new Map(); // facetKey -> index

    const pushField = ({ facetKey, label, options, groupCategoryId, isMultiple }) => {
      if (!facetKey) return;
      const key = String(facetKey);
      if (added.has(key)) {
        const idx = added.get(key);
        const prev = fields[idx];
        const merged = []; const seen = new Set();
        [...prev.options, ...(options || [])].forEach(v => {
          const u = String(v || '').toUpperCase().trim();
          if (!u || seen.has(u)) return; seen.add(u); merged.push(String(v));
        });
        fields[idx] = { ...prev, options: merged, groupCategoryId: prev.groupCategoryId ?? groupCategoryId, isMultiple: prev.isMultiple || !!isMultiple };
        return;
      }
      added.set(key, fields.length);
      fields.push({ facetKey: key, label: label || key, options: Array.isArray(options) ? options : [], groupCategoryId: groupCategoryId || null, isMultiple: !!isMultiple });
    };

    // 1) BACKEND categories (existing MySQL structure) - first pass
    const rootBackend = resolveBackendCategory(rootName);
    if (rootBackend) {
      const directChildren = getRawChildren(rootBackend.id);
      directChildren.forEach(groupCat => {
        const facetKey = getFacetKey(groupCat);
        if (!facetKey) return;
        const grandChildren = getRawChildren(groupCat.id);
        const leafDescendants = getAllLeaves(groupCat.id);
        const hasNestedStructure = grandChildren.some(gc => hasChildren(gc.id));
        const isMultiple = facetKey === 'fonctionnalite' || hasNestedStructure;
        const dynNames = leafDescendants.length > 0
          ? leafDescendants.map(l => l.name).filter(Boolean)
          : grandChildren.map(gc => gc.name).filter(Boolean);
        const templateVals = Array.isArray(facetTemplates[facetKey]) ? facetTemplates[facetKey] : [];
        const merged = []; const seen = new Set();
        [...dynNames, ...templateVals].forEach(v => {
          const u = String(v || '').toUpperCase().trim();
          if (!u || seen.has(u)) return;
          seen.add(u); merged.push(String(v));
        });
        pushField({ facetKey, label: groupCat.name, options: merged, groupCategoryId: groupCat.id, isMultiple });
      });
    }

    // 2) LOCAL STORAGE family tree (CATEGORY_TREE from AdminFilters) - ALWAYS included (live CRUD)
    const rootTree = findRootInCategoryTree(rootName);
    if (rootTree && Array.isArray(rootTree.children)) {
      rootTree.children.forEach(groupNode => {
        const facetKey = getFacetKey(groupNode?.name);
        if (!facetKey) return;
        const groupLeaves = collectTreeLeaves(groupNode);
        const directKidNames = (Array.isArray(groupNode.children) ? groupNode.children : []).map(c => c.name).filter(Boolean);
        const isMultiple = facetKey === 'fonctionnalite' || treeChildHasNested(groupNode);
        // Merge: leaves + direct children + facetTemplates
        const templateVals = Array.isArray(facetTemplates[facetKey]) ? facetTemplates[facetKey] : [];
        const merged = []; const seen = new Set();
        [...groupLeaves, ...directKidNames, ...templateVals].forEach(v => {
          const u = String(v || '').toUpperCase().trim();
          if (!u || seen.has(u)) return; seen.add(u); merged.push(String(v));
        });
        pushField({ facetKey, label: groupNode.name || FACET_LABEL_FR[facetKey] || facetKey, options: merged, groupCategoryId: null, isMultiple });
      });
    }

    // 3) Flat facetTemplates connues (marque, norme, etc.) - ajout automatique si renseignées dans AdminFilters
    Object.keys(FACET_LABEL_FR).forEach(k => {
      const tvals = Array.isArray(facetTemplates[k]) ? facetTemplates[k] : [];
      if (tvals.length === 0) return;
      pushField({
        facetKey: k, label: FACET_LABEL_FR[k] || k,
        options: tvals, groupCategoryId: null,
        isMultiple: k === 'fonctionnalite'
      });
    });

    // Fallback minimal si aucun champ trouvé (aucune structure configurée)
    if (fields.length === 0) {
      Object.keys(FACET_LABEL_FR).forEach(k => {
        const tvals = Array.isArray(facetTemplates[k]) ? facetTemplates[k] : [];
        if (tvals.length === 0) return;
        fields.push({
          facetKey: k, label: FACET_LABEL_FR[k] || k,
          options: tvals, groupCategoryId: null,
          isMultiple: k === 'fonctionnalite'
        });
      });
    }
    return fields;
  };
  const dynamicFacetFields = getDynamicFacetFields(selectedRootName);

  const getCategorySpecificFieldsById = categoryId => {
    if (!categoryId) return [];
    const cat = getCategoryById(categoryId);
    if (!cat?.specificFields) return [];
    let sf = cat.specificFields;
    if (typeof sf === 'string') { try { sf = JSON.parse(sf); } catch { sf = {}; } }
    if (!sf || typeof sf !== 'object') return [];
    return Object.entries(sf)
      .filter(([, cfg]) => cfg && typeof cfg === 'object')
      .map(([key, cfg]) => ({
        key, label: cfg.label || key, type: cfg.type || 'text',
        options: Array.isArray(cfg.options) ? cfg.options : [],
        required: cfg.required || false, isMultiple: cfg.type === 'multiselect'
      }));
  };

  /* ============================================================
     FORM HELPERS
     ============================================================ */
  const setField = (name, value) => {
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({ ...prev, [parent]: { ...(prev[parent] || {}), [child]: value } }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFacetChange = (facetKey, value, isMultiple = false) => {
    setFormData(prev => {
      const nextFacets = { ...(prev.facets || {}) };
      if (isMultiple) nextFacets[facetKey] = Array.isArray(value) ? value : (value ? [value] : []);
      else nextFacets[facetKey] = value == null ? '' : value;
      return { ...prev, facets: nextFacets };
    });
  };

  const handleArrayInputChange = (field, value) => {
    const v = typeof value === 'string' ? value : (Array.isArray(value) ? value.join('\n') : '');
    const items = String(v).split(/\r?\n/).map(item => item.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, [field]: items }));
  };

  const getArrayFieldAsText = field => {
    const raw = formData?.[field];
    if (Array.isArray(raw)) return raw.join('\n');
    if (typeof raw === 'string') return raw;
    if (raw == null) return '';
    return String(raw);
  };

  const removeImage = index => setImages(prev => prev.filter((_, i) => i !== index));
  const setMainImage = index => setImages(prev => prev.map((image, i) => ({ ...image, isMain: i === index })));
  const handleDocumentUpload = (type, file) => setDocuments(prev => ({ ...prev, [type]: { file, isExisting: false } }));

  /* ============================================================
     EFFETS & CHARGEMENTS
     ============================================================ */
  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories({ includeInactive: false });
      setCategories(response.categories || []);
    } catch (err) { console.error('Erreur chargement catégories:', err); }
  };

  const buildCascadesFromCategoryArray = catIdsArray => {
    if (!Array.isArray(catIdsArray) || catIdsArray.length === 0) { setCascadePath([]); return; }
    for (const cid of catIdsArray) {
      const path = findBusinessPathFromBackendId(cid);
      if (path && path.length > 0) { setCascadePath(path); syncFinalCategory(path); return; }
    }
  };

  const loadProduct = async () => {
    try {
      setIsLoadingData(true);
      const response = await productService.getProductById(id);
      const rawProduct = response.product;
      const p = cleanDisplay(rawProduct); // ← NETTOIE TOUT l'objet (strings, nested obj, arrays, facets keys/vals, features, etc.)
      const rawFacets = p.facets || {};
      let parsedFacets = typeof rawFacets === 'string'
        ? (() => { try { return JSON.parse(rawFacets); } catch { return {}; } })()
        : rawFacets;
      parsedFacets = cleanDisplay({ ...(parsedFacets || {}) });
      Object.keys(parsedFacets).forEach(k => { if (parsedFacets[k] === undefined) parsedFacets[k] = ''; });

      const rawFeatures = p.features; let finalFeatures = [];
      if (Array.isArray(rawFeatures)) finalFeatures = cleanDisplay(rawFeatures.filter(Boolean));
      else if (typeof rawFeatures === 'string') {
        try {
          let parsed = JSON.parse(rawFeatures);
          if (!Array.isArray(parsed)) throw 0;
          finalFeatures = cleanDisplay(parsed);
        } catch {
          finalFeatures = cleanDisplay(
            rawFeatures.split(/\r?\n/).map(x => x.trim()).filter(Boolean)
          );
        }
      }
      let finalTechSpecs = p.technicalSpecs || {};
      if (typeof finalTechSpecs === 'string') { try { finalTechSpecs = JSON.parse(finalTechSpecs); } catch { finalTechSpecs = {}; } }
      if (!finalTechSpecs || typeof finalTechSpecs !== 'object') finalTechSpecs = {};
      finalTechSpecs = cleanDisplay(finalTechSpecs);
      const rawSeo = p.seoKeywords; let finalSeo = [];
      if (Array.isArray(rawSeo)) finalSeo = cleanDisplay(rawSeo.filter(Boolean));
      else if (typeof rawSeo === 'string') finalSeo = cleanDisplay(rawSeo.split(',').map(x => x.trim()).filter(Boolean));

      setFormData({
        name: p.name || '', reference: p.reference || '',
        description: p.description || '', shortDescription: p.shortDescription || '',
        categories: p.categories?.map(c => c.id) || [],
        status: p.status || 'draft', isVisible: !!p.isVisible, isFeatured: !!p.isFeatured,
        features: finalFeatures, technicalSpecs: finalTechSpecs, facets: parsedFacets,
        metaTitle: p.metaTitle || '', metaDescription: p.metaDescription || '', seoKeywords: finalSeo
      });

      const catIds = (p.categories || []).map(c => c.id).filter(Boolean);
      if (catIds.length > 0) setTimeout(() => buildCascadesFromCategoryArray(catIds), 50);

      if (p.images) {
        const existingImages = [];
        if (p.images.main) existingImages.push({ url: p.images.main, isMain: true, isExisting: true });
        if (p.images.gallery) p.images.gallery.forEach(img => existingImages.push({ url: img, isMain: false, isExisting: true }));
        setImages(existingImages);
      }
      if (p.documents) {
        setDocuments({
          datasheet: p.documents.datasheet ? { url: p.documents.datasheet, isExisting: true } : null,
          manual: p.documents.manual ? { url: p.documents.manual, isExisting: true } : null,
          certificate: p.documents.certificate ? { url: p.documents.certificate, isExisting: true } : null
        });
      }

      if (Array.isArray(p.attributes) && p.attributes.length > 0) {
        const mapped = p.attributes
          .filter(a => a && typeof a === 'object')
          .map(a => ({
            criterion: String(a.criterion != null ? a.criterion : (a.name || '')).trim(),
            value: String(a.value != null ? a.value : '').trim()
          }))
          .filter(a => a.criterion !== '');
        if (mapped.length > 0) setDynAttrRows(mapped);
      }
    } catch (err) {
      console.error('Erreur chargement produit:', err);
      setError('Erreur lors du chargement du produit');
    } finally { setIsLoadingData(false); }
  };

  useEffect(() => { loadCategories(); }, [id, facetTemplatesVersion]);

  // 🔁 Polling catégorie backend TOUTES LES 1.5s (rafraîchit familles backend)
  //    → Plus jamais "famille créée pas à jour" dans Nouveau Produit.
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const resp = await categoryService.getCategories({ includeInactive: false });
        const next = Array.isArray(resp?.categories) ? resp.categories : [];
        if (next.length === 0) return;
        setCategories(prev => {
          if (prev.length === next.length) {
            const pids = new Set((prev || []).map(x => String(x.id) + '|' + String(x.name) + '|' + String(x.parentCategoryId || 0) + '|' + (x.updatedAt || '')));
            let same = true;
            for (const n of next) {
              const sig = String(n.id) + '|' + String(n.name) + '|' + String(n.parentCategoryId || 0) + '|' + (n.updatedAt || '');
              if (!pids.has(sig)) { same = false; break; }
            }
            if (same) return prev;
          }
          setBackendCategoryVersion(v => v + 1);
          return next;
        });
      } catch { /* ignore polling errors */ }
    }, 1500);
    return () => clearInterval(iv);
  }, [id]);

  // --- Validation du cascadePath quand l'arbre FAMILLES change (CRUD AdminFilters)
  //     Si un nœud sélectionné a été SUPPRIMÉ / RENOMMÉ dans AdminFilters,
  //     on coupe le chemin au dernier parent encore valide.
  useEffect(() => {
    setCascadePath(prev => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      let validUpTo = -1;
      for (let i = 0; i < prev.length; i++) {
        const pathSlice = prev.slice(0, i + 1);
        const node = getBusinessNodeAtPath(pathSlice);
        if (!node) break;
        validUpTo = i;
      }
      if (validUpTo === prev.length - 1) return prev; // tout valide, rien à faire
      const newPath = validUpTo >= 0 ? prev.slice(0, validUpTo + 1) : [];
      if (newPath.length > 0) syncFinalCategory(newPath);
      return newPath;
    });
  }, [familyTreeVersion, backendCategoryVersion]);

  useEffect(() => {
    if (isEdit) { if (categories.length > 0) loadProduct(); }
    else {
      if (cascadePath.length === 0 && categories.length > 0) {
        try {
          const lastDraftStr = localStorage.getItem('domoelec:product:lastDraft');
          if (lastDraftStr) {
            const lastDraft = JSON.parse(lastDraftStr);
            if (Array.isArray(lastDraft.cascadePath) && lastDraft.cascadePath.length > 0) {
              const allNamesValid = lastDraft.cascadePath.every(nm =>
                !!getBusinessNodeAtPath(
                  lastDraft.cascadePath.slice(0, lastDraft.cascadePath.indexOf(nm) + 1)
                )
              );
              if (allNamesValid) {
                setCascadePath(lastDraft.cascadePath);
                syncFinalCategory(lastDraft.cascadePath);
              }
            }
          }
        } catch { /* ignore */ }
      }
    }
  }, [id, categories.length]);

  useEffect(() => {
    if (isEdit) return;
    try {
      const existingStr = localStorage.getItem('domoelec:product:lastDraft');
      const existing = existingStr ? JSON.parse(existingStr) : {};
      const merged = {
        ...existing,
        cascadePath: [...cascadePath], facets: formData.facets || {},
        name: formData.name, reference: formData.reference,
        description: formData.description, shortDescription: formData.shortDescription,
        savedAt: Date.now()
      };
      localStorage.setItem('domoelec:product:lastDraft', JSON.stringify(merged));
    } catch { /* ignore */ }
  }, [cascadePath, formData.facets, formData.name, formData.reference, formData.description, formData.shortDescription, isEdit]);

  useEffect(() => {
    if (isEdit || categories.length === 0 || formData.name) return;
    try {
      const str = localStorage.getItem('domoelec:product:lastDraft');
      if (!str) return;
      const d = JSON.parse(str);
      setFormData(prev => ({
        ...prev,
        name: prev.name || d.name || '',
        reference: prev.reference || d.reference || '',
        shortDescription: prev.shortDescription || d.shortDescription || '',
        description: prev.description || d.description || '',
        facets: { ...(d.facets || {}), ...(prev.facets || {}) }
      }));
    } catch { /* ignore */ }
  }, [categories.length, isEdit]);

  useEffect(() => {
    if (!formData.categories || formData.categories.length === 0) {
      setDynBranches([]);
      setDynBranchSelections({});
      setDynFamilyId(null);
      return;
    }
    (async () => {
      try {
        let rootId = null;
        for (const cid of formData.categories) {
          const cat = (categories || []).find(c => c.id === cid);
          if (cat && !cat.parentCategoryId && !cat.parentCategory?.id) {
            rootId = cid;
            break;
          }
        }
        if (!rootId) return;
        rootId = Number(rootId);
        if (rootId === dynFamilyId) return;
        setDynLoading(true);
        setDynFamilyId(rootId);
        const { family } = await categoryService.getFamily(rootId, { includeInactive: true });
        if (family?.children?.length) setDynBranches(family.children);
        else setDynBranches([]);
        const selections = {};
        family?.children?.forEach?.(crit => {
          const q = [...(crit.children || [])];
          while (q.length) {
            const n = q.shift();
            if (formData.categories.includes(n.id)) {
              selections[crit.id] = n.id;
              break;
            }
            if (n.children?.length) q.push(...n.children);
          }
        });
        setDynBranchSelections(selections);
      } catch (_) {
      } finally {
        setDynLoading(false);
      }
    })();
  }, [JSON.stringify(formData.categories || []), categories]);

  const flattenBranchOptions = (branchRoot) => {
    const out = [];
    const walk = (nodes, depth = 0) => {
      (nodes || []).forEach(n => {
        out.push({
          value: n.id,
          label: `${'— '.repeat(depth)}${n.type === 'value' ? '🍃 ' : ''}${n.name}`,
          depth
        });
        if (n.children?.length) walk(n.children, depth + 1);
      });
    };
    if (branchRoot?.children?.length) walk(branchRoot.children, 1);
    else if (branchRoot && branchRoot.id && (branchRoot.type === 'criterion' || !branchRoot.type)) {
      if (branchRoot.children?.length) walk(branchRoot.children, 1);
    }
    return out;
  };

  const handleDynBranchChange = async (criterionId, nodeId) => {
    setDynBranchSelections(prev => ({ ...prev, [criterionId]: nodeId }));
    (async () => {
      try {
        const crit = dynBranches.find(b => b.id === criterionId);
        if (!crit) return;
        const descendantIds = new Set();
        const q = [crit];
        while (q.length) {
          const x = q.shift();
          descendantIds.add(x.id);
          if (x.children?.length) q.push(...x.children);
        }
        const currentCats = Array.isArray(formData.categories) ? [...formData.categories] : [];
        let newCats = currentCats.filter(id => !descendantIds.has(Number(id)));
        if (nodeId) newCats.push(Number(nodeId));
        newCats = Array.from(new Set(newCats.filter(x => typeof x === 'number' || !isNaN(Number(x))).map(Number)));
        setFormData(p => ({ ...p, categories: newCats }));
        if (crit && nodeId) {
          const selectedNodeName = (() => {
            const f = flattenBranchOptions(crit);
            return f.find(o => o.value === nodeId)?.label?.replace(/^[—🍃 ]+/g, '') || null;
          })();
          const facetKey = crit.slug || `branch_${criterionId}`;
          setFormData(p => ({ ...p, facets: { ...(p.facets || {}), [facetKey]: selectedNodeName } }));
        }
      } catch (e) {}
    })();
  };

  // ==========================================================================
  // 🔑 NOUVEAU : Helpers Cascade FAMILLE → CRITÈRES → VALEURS (sans mélange)
  // Stockage : facets._familleCriteres = { famille, items: [{nom,valeur}] }
  //            + facets._familyCriteresFlat = [{famille,critere,valeur}]   (compat)
  // ==========================================================================
  const FAM_CRIT_KEY = '_familleCriteres';
  const FAM_CRIT_FLAT_KEY = '_familyCriteresFlat';

  const getCurrentFamilleCriteres = () => {
    const facets = formData.facets || {};
    const bloc = facets[FAM_CRIT_KEY];
    if (bloc && Array.isArray(bloc.items)) return bloc;
    // Fallback flat : reconstruction depuis _familyCriteresFlat
    const flat = Array.isArray(facets[FAM_CRIT_FLAT_KEY]) ? facets[FAM_CRIT_FLAT_KEY] : [];
    if (flat.length > 0) {
      const premiereFamille = (flat.find(x => x && x.famille) || {}).famille || selectedRootName || '';
      return { famille: premiereFamille, items: flat.filter(x => x && (x.critere || x.valeur)).map(x => ({ nom: x.critere || '', valeur: x.valeur || '' })) };
    }
    return { famille: selectedRootName || '', items: [] };
  };

  const getFamCritSelection = (nomCrit) => {
    const bloc = getCurrentFamilleCriteres();
    const it = (bloc.items || []).find(x => x && String(x.nom) === String(nomCrit));
    return it ? it.valeur : '';
  };

  const setFamilleName = (nouvelleFamille) => {
    setFormData(p => {
      const facets = { ...(p.facets || {}) };
      // RESET AUTO des items quand la famille change (règle stricte anti-mélange)
      const items = [];
      facets[FAM_CRIT_KEY] = { famille: nouvelleFamille || '', items };
      facets[FAM_CRIT_FLAT_KEY] = items.map(it => ({ famille: nouvelleFamille || '', critere: it.nom || '', valeur: it.valeur || '' }));
      return { ...p, facets };
    });
  };

  const setFamilleCritereValeur = (familleNom, nomCritere, valeur) => {
    setFormData(p => {
      const facets = { ...(p.facets || {}) };
      const bloc = facets[FAM_CRIT_KEY] ? { ...facets[FAM_CRIT_KEY] } : { famille: familleNom || '', items: [] };
      bloc.famille = familleNom || bloc.famille || '';
      const items = [...(bloc.items || [])];
      const idx = items.findIndex(x => x && String(x.nom) === String(nomCritere));
      if (!valeur) {
        if (idx >= 0) items.splice(idx, 1);
      } else if (idx >= 0) {
        items[idx] = { ...items[idx], nom: nomCritere, valeur };
      } else {
        items.push({ nom: nomCritere, valeur });
      }
      bloc.items = items;
      facets[FAM_CRIT_KEY] = bloc;
      facets[FAM_CRIT_FLAT_KEY] = items.map(it => ({ famille: bloc.famille, critere: it.nom || '', valeur: it.valeur || '' }));
      return { ...p, facets };
    });
  };

  const handleGenerateIA = async (isRegenerate = false) => {
    if (iaGenerating) return;
    const hasInfo = Boolean(formData.name?.trim?.() || formData.shortDescription?.trim?.() || formData.categories?.length || selectedRootName);
    if (!hasInfo) {
      appMsg.warning('Veuillez renseigner au moins le nom, la description courte ou une catégorie avant de générer.');
      return;
    }
    if (!isRegenerate && formData.description?.trim?.() && !window.confirm('La description actuelle sera remplacée. Continuer ?')) return;
    setIaGenerating(true);
    try {
      const catsFlat = new Set();
      (formData.categories || []).forEach(cid => {
        const c = (categories || []).find(x => String(x.id) === String(cid));
        if (c?.name) catsFlat.add(c.name);
      });
      const { description } = await productService.generateDescription({
        name: formData.name,
        shortDescription: formData.shortDescription,
        categorySlugs: Array.from(catsFlat).map(n => String(n).toLowerCase().replace(/[^a-z0-9]+/g, '-')),
        categoryNames: Array.from(catsFlat),
        facets: formData.facets || {},
        features: Array.isArray(formData.features) ? formData.features : [],
      });
      setFormData(p => ({ ...p, description: description || '' }));
      appMsg.success('Description générée avec succès');
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || err.message || "Erreur génération IA";
      appMsg.error(msg);
    } finally {
      setIaGenerating(false);
    }
  };

  /* ============================================================
     SOUMISSION
     ============================================================ */
  const validate = () => {
    const msgs = [];
    if (!String(formData.name || '').trim()) msgs.push('Nom du produit requis');
    if (!String(formData.reference || '').trim()) msgs.push('Référence produit requise');

    if (!effectiveCategory || !isFinalSelection || cascadePath.length === 0) {
      if (cascadePath.length === 0) msgs.push('Aucune classification : sélectionnez une FAMILLE puis descendez jusqu\'à la FEUILLE finale');
      else if (!isFinalSelection) msgs.push(`Classification incomplète : terminez le parcours "${cascadePath.join(' → ')} → ..."`);
      else if (fallbackUsed) {
        // Cas feuille custom pas créée BDD → fallback sur dernier résolu = valide. Pas bloquant, alert warning séparée affichée.
      } else if (!lastResolvedBackend) {
        msgs.push('Aucun nœud de la classification trouvé dans la base. Créez-les d\'abord dans le menu Catégories.');
      } else {
        msgs.push('Classification incomplète dans la base (dernier nœud trouvé : ' + (lastResolvedBackend?.name || '?') + '). Créez la catégorie feuille dans Catégories.');
      }
    }

    const categoryIdForFields = effectiveCategory?.id ?? (fallbackUsed ? lastResolvedBackend?.id : null);
    [...specificFields, ...(categoryIdForFields ? getCategorySpecificFieldsById(Number(categoryIdForFields)) : [])]
      .filter(f => f.required)
      .forEach(f => {
        const v = formData.technicalSpecs?.[f.key];
        if (v === undefined || v === null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === '')) {
          msgs.push(`Champ requis : ${f.label}`);
        }
      });

    if (msgs.length > 0) {
      setError('Données invalides : ' + msgs.join(' • '));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e, goStatus = null) => {
    if (e) e.preventDefault();
    if (!validate()) return;
    setIsLoading(true); setError(null);
    try {
      const payload = { ...formData };
      if (goStatus) payload.status = goStatus;

      if (effectiveCategory?.id) {
        payload.categories = [Number(effectiveCategory.id)];
        payload.categoriesObjects = [{ id: Number(effectiveCategory.id), name: effectiveCategory.name }];
        // Si fallback feuille custom (pas en BDD) : ajouter le chemin métier COMPLET + nom feuille pour retrouver produit
        if (fallbackUsed) {
          payload.categoryPathText = cascadePath.join(' > ');
          payload.facetLeafName = String(cascadePath[cascadePath.length - 1] || '').trim() || null;
        }
      } else if (isEdit && Array.isArray(formData.categories) && formData.categories.length > 0) {
        payload.categories = formData.categories.map(id => Number(id)).filter(Boolean);
      } else {
        payload.categories = [];
      }

      payload.features = Array.isArray(formData.features) ? formData.features.filter(f => String(f || '').trim()) : [];
      payload.seoKeywords = Array.isArray(formData.seoKeywords) ? formData.seoKeywords.filter(Boolean) : [];
      payload.technicalSpecs = formData.technicalSpecs && typeof formData.technicalSpecs === 'object' && !Array.isArray(formData.technicalSpecs)
        ? formData.technicalSpecs : {};
      payload.facets = formData.facets && typeof formData.facets === 'object' && !Array.isArray(formData.facets)
        ? formData.facets : {};
      payload.attributes = Array.isArray(dynAttrRows)
        ? dynAttrRows
            .map(r => ({
              criterion: String(r.criterion == null ? '' : r.criterion).trim(),
              value: String(r.value == null ? '' : r.value).trim()
            }))
            .filter(r => r.criterion !== '')
        : [];
      payload.price = (formData.price != null && !isNaN(Number(formData.price))) ? Number(formData.price) : null;

      const filesNouvelles = [];
      const urlsExistantes = [];
      let mainUrl = null;
      for (const img of images) {
        if (img.file instanceof File) {
          filesNouvelles.push(img.file);
          if (img.isMain && !mainUrl) mainUrl = '__NEW_' + (filesNouvelles.length - 1);
        } else if (typeof img.url === 'string' && img.url) {
          urlsExistantes.push(img.url);
          if (img.isMain) mainUrl = img.url;
        }
      }
      if (!mainUrl) {
        if (urlsExistantes.length > 0) mainUrl = urlsExistantes[0];
        else if (filesNouvelles.length > 0) mainUrl = '__NEW_0';
      }
      payload.images = filesNouvelles;
      payload.imagesExisting = {
        main: mainUrl && !mainUrl.startsWith('__NEW_') ? mainUrl : null,
        gallery: urlsExistantes.filter(u => u !== mainUrl),
        firstNewIsMain: filesNouvelles.length > 0 && mainUrl && mainUrl.startsWith('__NEW_')
          ? Number(mainUrl.replace('__NEW_', '')) : null
      };
      payload.mainImage = payload.imagesExisting.main;
      payload.galleryImages = payload.imagesExisting.gallery;

      const docsFiles = {};
      const docsUrls = {};
      for (const type of ['datasheet', 'manual', 'certificate']) {
        const doc = documents[type];
        if (!doc) continue;
        if (doc.file instanceof File) docsFiles[type] = doc.file;
        else if (typeof doc.url === 'string' && doc.url) docsUrls[type] = doc.url;
      }
      payload.documents = docsFiles;
      payload.documentsExisting = docsUrls;

      if (isEdit) {
        await productService.updateProduct(id, payload);
      } else {
        await productService.createProduct(payload);
      }

      localStorage.removeItem('domoelec:product:lastDraft');
      navigate('/admin/products');
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      const data = err?.response?.data;
      let msg = data?.message || data?.error || 'Erreur lors de la sauvegarde du produit';
      const errorsList = Array.isArray(data?.errors) ? data.errors : (data?.errors && typeof data.errors === 'object' ? Object.entries(data.errors).map(([k, v]) => `${k} : ${Array.isArray(v) ? v.join(', ') : v}`) : []);
      const msgsFields = [
        ...(data?.name ? (Array.isArray(data.name) ? data.name : [data.name]) : []),
        ...(data?.reference ? (Array.isArray(data.reference) ? data.reference : [data.reference]) : []),
        ...(data?.categories ? (Array.isArray(data.categories) ? data.categories : [data.categories]) : []),
        ...(data?.features ? (Array.isArray(data.features) ? data.features : [data.features]) : [])
      ];
      const allDetails = [...errorsList, ...msgsFields].filter(Boolean);
      if (allDetails.length > 0) msg = msg + ' — Détails : ' + allDetails.join(' • ');
      setError(msg);
    } finally { setIsLoading(false); }
  };

  const clearDraft = () => {
    localStorage.removeItem('domoelec:product:lastDraft');
    setCascadePath([]);
    setFormData(prev => ({
      ...prev,
      categories: [], facets: {},
      name: '', reference: '', description: '', shortDescription: ''
    }));
  };

  /* ============================================================
     RENDU
     ============================================================ */
  if (isLoadingData) return <Alert type="info" showIcon title="Chargement du produit..." style={{ margin: 24 }} />;

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space style={{ marginBottom: 16 }} orientation="horizontal" size="middle" wrap align="center">
          <Title level={3} style={{ margin: 0 }}>{isEdit ? 'Modifier le produit' : 'Nouveau produit'}</Title>
          {!isEdit && (
            <Popconfirm
              title="Effacer le brouillon en cours ?"
              description="Toutes les données non sauvegardées seront perdues."
              okText="Effacer" cancelText="Annuler" okButtonProps={{ danger: true }}
              onConfirm={clearDraft}
            >
              <Button size="small" danger>🗑️ Effacer brouillon</Button>
            </Popconfirm>
          )}
        </Space>

        {error && (<Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} closable onClose={() => setError(null)} />)}

        <Form layout="vertical" onSubmitCapture={e => handleSubmit(e)}>
          {/* 1) INFORMATIONS GÉNÉRALES */}
          <Divider titlePlacement="left">📋 Informations générales</Divider>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Nom du produit *" required>
                <Input name="name" value={formData.name} onChange={e => setField('name', e.target.value)} placeholder="Ex: Bloc évacuation 2W autonome" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Référence *" required>
                <Input name="reference" value={formData.reference} onChange={e => setField('reference', e.target.value)} placeholder="Ex: BA-EVAC-2W-001" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Description courte">
                <TextArea rows={2} name="shortDescription" value={formData.shortDescription} onChange={e => setField('shortDescription', e.target.value)} placeholder="Une phrase résumant le produit (affichée dans les listings)" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label={
                  <span style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    Description détaillée
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      loading={iaGenerating}
                      disabled={iaGenerating}
                      style={{ borderColor: '#b7700f', color: '#b7700f' }}
                      onClick={handleGenerateIA}
                      title={iaGenerating ? 'Génération en cours...' : 'Générer la description avec IA'}
                    >
                      <span style={{ fontSize: 16 }}>🤖</span>
                    </Button>
                    <Space size={6} style={{ marginLeft: 'auto' }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Aperçu</Text>
                      <Switch
                        size="small"
                        checked={!descPreviewMode}
                        onChange={(c) => setDescPreviewMode(!c)}
                        checkedChildren={<>HTML</>}
                        unCheckedChildren={<>WYSIWYG</>}
                      />
                      <Text type="secondary" style={{ fontSize: 12 }}>Source HTML</Text>
                    </Space>
                  </span>
                }
              >
                {descPreviewMode ? (
                  <div
                    onDoubleClick={() => setDescPreviewMode(false)}
                    style={{
                      minHeight: 180,
                      maxHeight: 320,
                      overflow: 'auto',
                      padding: '12px 14px',
                      border: '1px solid #10b981',
                      borderRadius: 8,
                      background: '#f6fffb',
                      fontSize: 14,
                      lineHeight: 1.55,
                      color: '#111827',
                      cursor: 'text'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formData.description
                        ? String(formData.description)
                            .replace(/^\s+|\s+$/g, '')
                        : '<div style="color:#94a3b8;font-style:italic">Double-cliquez ici pour modifier le code HTML source...</div>'
                    }}
                  />
                ) : (
                  <TextArea
                    rows={8}
                    name="description"
                    value={formData.description || ''}
                    onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                    placeholder="Description détaillée du produit (caractéristiques, atouts, utilisations...)"
                    disabled={isLoading}
                    style={{ fontFamily: 'Consolas, "Courier New", monospace', fontSize: 13 }}
                  />
                )}
                {!formData.description && (
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, marginTop: 6 }}
                    onClick={() => handleGenerateIA(true)}
                  >
                    🔄 Régénérer la description
                  </Button>
                )}
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Statut">
                <Select value={formData.status} onChange={v => setField('status', v)}
                  options={[{ value: 'draft', label: '📝 Brouillon' }, { value: 'published', label: '✅ Publié' }, { value: 'archived', label: '📦 Archivé' }]} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Visible sur le site"><Switch checked={formData.isVisible} onChange={v => setField('isVisible', v)} /></Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Produit mis en avant"><Switch checked={formData.isFeatured} onChange={v => setField('isFeatured', v)} /></Form.Item>
            </Col>
          </Row>

          {/* 2) CLASSIFICATION ARBRE MÉTIER */}
          <Divider titlePlacement="left">📂 Classification du produit</Divider>

          {unresolvedSelectedNames.length > 0 && (
            <Alert type="warning" style={{ marginBottom: 12 }} showIcon title={`⚠️ Certains noms métier n'ont pas été trouvés dans la base : ${unresolvedSelectedNames.join(', ')}`}
              description="Cela peut arriver si la base n'a pas encore ces catégories. La sauvegarde utilise la dernière catégorie résolue."
            />
          )}

          {cascadePathObjects.length > 0 && (
            <Breadcrumb
              style={{ marginBottom: 12 }}
              items={cascadePathObjects.map((c, i) => ({
                key: `bc-${i}-${c?.id ?? i}`,
                title: c?.name || cascadePath[i] || '?',
              }))}
            />
          )}

          <Row gutter={[12, 12]}>
            {/* 1) FAMILLE idx 0 — utilise MERGED tree (backend famille à jour PRIORITÉ 1) */}
            <Col xs={24} md={8}>
              <Text strong><span style={{ color: '#0369a1' }}>1.</span> FAMILLE *</Text>
              <Select
                value={cascadePath[0] || undefined}
                onChange={v => selectFamily(v)}
                style={{ width: '100%' }}
                options={CATEGORY_TREE_MERGED.map(c => ({
                  value: c.name,
                  label: c.name + ((c.children && c.children.length > 0) ? ' →' : ' ✓') + (c._isBackend ? ' 🆕' : '')
                }))}
                placeholder="Sélectionner une famille..." allowClear showSearch
                filterOption={(inp, opt) => (opt.label || '').toString().toLowerCase().includes(inp.toLowerCase())}
              />
            </Col>

            {/* 2) Niveaux déjà remplis idx≥1 */}
            {cascadePath.slice(1).map((nodeName, offset) => {
              const idx = offset + 1;
              const step = idx + 1;
              const color = step === 2 ? '#0284c7' : (step === 3 ? '#0891b2' : '#0e7490');
              const prevPath = cascadePath.slice(0, idx);
              const opts = getBusinessChildren(prevPath);
              const parentName = cascadePath[idx - 1] || '';
              return (
                <Col xs={24} md={8} key={`cascade-${idx}-${nodeName}`}>
                  <Text strong>
                    <span style={{ color }}>{step}.</span> {(parentName ? String(parentName).toUpperCase() : 'SOUS-CATÉGORIE')} *
                  </Text>
                  <Select
                    value={nodeName} onChange={v => selectCascadeLevel(idx, v)}
                    style={{ width: '100%' }}
                    options={opts.map(c => ({ value: c.name, label: c.name + ((c.children && c.children.length > 0) ? ' →' : ' ✓') }))}
                    placeholder="Sélectionner..." allowClear showSearch
                    filterOption={(inp, opt) => (opt.label || '').toString().toLowerCase().includes(inp.toLowerCase())}
                  />
                </Col>
              );
            })}

            {/* 3) Niveau SUIVANT vide */}
            {selectedRootName && !isFinalSelection && nextBusinessOptions.length > 0 && (
              (() => {
                const step = cascadePath.length + 1;
                const color = step === 2 ? '#0284c7' : (step === 3 ? '#0891b2' : '#0e7490');
                const parentName = cascadePath[cascadePath.length - 1] || '';
                return (
                  <Col xs={24} md={8}>
                    <Text strong>
                      <span style={{ color }}>{step}.</span> {(parentName ? String(parentName).toUpperCase() : 'SOUS-CATÉGORIE')} *
                    </Text>
                    <Select
                      value={undefined}
                      onChange={v => selectCascadeLevel(cascadePath.length, v)}
                      style={{ width: '100%' }}
                      options={nextBusinessOptions.map(c => ({
                        value: c.name,
                        label: c.name + ((c.children && c.children.length > 0) ? ' →' : ' ✓')
                      }))}
                      placeholder="Choisir la valeur..." showSearch
                      filterOption={(inp, opt) => (opt.label || '').toString().toLowerCase().includes(inp.toLowerCase())}
                    />
                  </Col>
                );
              })()
            )}

            {isFinalSelection && (
              <Col xs={24}>
                <Alert
                  type={fallbackUsed ? 'warning' : 'success'}
                  showIcon
                  title={
                    fallbackUsed
                      ? `🟡 Classification BDD incomplète — "${lastResolvedBackend?.name || '?'}"  utilisé + métier: ${cascadePath.join('  →  ')}`
                      : (effectiveCategory
                        ? `✅ Classification finale : ${cascadePath.join('  →  ')}  (ID catégorie #${effectiveCategory.id})`
                        : `✅ Classification métier finale : ${cascadePath.join('  →  ')}`)
                  }
                  description={
                    fallbackUsed
                      ? 'Attention : la/les catégorie(s) suivantes n\'existent PAS EN BDD : ' + unresolvedSelectedNames.join(', ') + '. Produit classé sur le dernier parent résolu. Créez les catégories manquantes dans Menu → Catégories pour un ciblage parfait.'
                      : 'La sélection est complète. Renseignez maintenant les champs spécifiques, facettes et images.'
                  }
                />
              </Col>
            )}
          </Row>

          {/* 2-BIS) CATÉGORIE ADMINFILTERS — sélection rapide OPTGROUPS */}
          <Divider titlePlacement="left">🏠 Catégorie AdminFilters (sélection rapide)</Divider>
          <Row gutter={[12, 12]}>
            <Col xs={24} md={24}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                title="Sélectionnez ici pour classer votre produit en 1 clic (fusion BDD + AdminFilters). Les champs en dessous s'actualisent."
              />
            </Col>
            <Col xs={24} md={24}>
              <Select
                style={{ width: '100%' }}
                showSearch
                placeholder="Choisir une catégorie : Catégories Backend OU Organigramme AdminFilters..."
                optionFilterProp="label"
                value={undefined}
                onChange={(val, option) => {
                  const pathArr = option?.nodePath || (option?.backendPath ? option.backendPath.split(' > ') : []);
                  if (Array.isArray(pathArr) && pathArr.length > 0) {
                    setCascadePath(pathArr.filter(Boolean));
                  } else if (typeof val === 'string' && val.includes(' > ')) {
                    setCascadePath(val.split(' > ').map(s => String(s).trim()).filter(Boolean));
                  } else if (typeof val === 'string' && val) {
                    setCascadePath([val]);
                  }
                }}
                options={[
                  {
                    label: '📂 Catégories Backend (base de données)',
                    title: '📂 Catégories Backend',
                    options: (() => {
                      const rootsBackend = categories.filter(c => !(c.parentCategoryId || c.parentCategory?.id));
                      const flat = [];
                      const walk = (cats, prefix = '') => {
                        cats.forEach(c => {
                          const label = prefix ? `${prefix} > ${c.name}` : String(c.name);
                          flat.push({
                            value: `backend_${c.id}`,
                            label: label,
                            backendPath: label,
                            nodePath: label.split(' > ')
                          });
                          const children = getRawChildren(c.id);
                          if (children.length > 0) walk(children, label);
                        });
                      };
                      walk(rootsBackend);
                      return flat;
                    })()
                  },
                  {
                    label: '🏠 Organigramme AdminFilters (Filtres / Familles)',
                    title: '🏠 Organigramme AdminFilters',
                    options: flattenAdminTree().map(o => ({
                      ...o,
                      label: o.label + (o.nodePath.length > 1 ? '' : ' (FAMILLE)')
                    }))
                  }
                ]}
              />
            </Col>
          </Row>

          {/* 2-TER) FACETTES ADMINFILTERS PARALLÈLES (mergedOptions) */}
          {(() => {
            const items = FACET_ADMIN_DEFS.filter(f => {
              const fromAdmin = Array.isArray(sharedTemplates[f.k]) ? sharedTemplates[f.k] : [];
              // Compte aussi les valeurs existantes du produit pour ne pas cacher les facets utilisées
              const existingVal = (formData.facets && formData.facets[f.k] != null) ? formData.facets[f.k] : null;
              const hasData = fromAdmin.length > 0
                || (Array.isArray(existingVal) ? existingVal.length > 0 : !!existingVal);
              return hasData;
            });
            if (items.length === 0) return null;
            const handleFacetMergedChange = (key, val, isMultiple) => {
              const next = { ...(formData.facets || {}) };
              if (isMultiple) next[key] = Array.isArray(val) ? val : [];
              else next[key] = val || null;
              setFormData(prev => ({ ...prev, facets: next }));
            };
            return (
              <>
                <Divider titlePlacement="left">🔍 Facettes AdminFilters (valeurs fusionnées)</Divider>
                <Alert type="info" showIcon style={{ marginBottom: 12 }}
                  title="Ces listes reprennent les valeurs ajoutées dans Admin → Filtres, fusionnées avec celles déjà présentes dans ce produit. Modifications temps réel (< 1.5s)."
                />
                <Row gutter={16}>
                  {items.map(f => {
                    const fromAdmin = Array.isArray(sharedTemplates[f.k]) ? sharedTemplates[f.k] : [];
                    const currentVal = formData.facets?.[f.k];
                    const currentArr = f.isMultiple
                      ? (Array.isArray(currentVal) ? currentVal : [])
                      : (currentVal != null ? [String(currentVal)] : []);
                    const mergedUniq = Array.from(new Set([
                      ...fromAdmin,
                      ...currentArr
                    ].filter(v => v != null && String(v).trim() !== '')));
                    if (mergedUniq.length === 0 && (!currentArr || currentArr.length === 0)) return null;
                    return (
                      <Col xs={24} md={8} key={`merged-facet-${f.k}`}>
                        <Form.Item label={<Space size={6}>{f.icon} <strong>{f.label}</strong>{f.isMultiple ? <Tag color="blue" style={{ margin: 0 }}>multi</Tag> : null}</Space>}>
                          <Select
                            mode={f.isMultiple ? 'multiple' : undefined}
                            allowClear
                            showSearch
                            value={currentVal}
                            onChange={(v) => handleFacetMergedChange(f.k, v, f.isMultiple)}
                            options={mergedUniq.map(v => ({ value: v, label: String(v) }))}
                            placeholder={`Choisir ${f.label}${f.isMultiple ? 's' : ''}...`}
                            filterOption={(inp, opt) => (opt.label || '').toString().toLowerCase().includes(inp.toLowerCase())}
                            style={{ width: '100%' }}
                          />
                        </Form.Item>
                      </Col>
                    );
                  })}
                </Row>
              </>
            );
          })()}

          <Card
            title={
              <Space>
                <span style={{ fontSize: 16 }}>🧩 Critères propres au Produit (dynamiques)</span>
                <Tag color="green" style={{ marginLeft: 4 }}>
                  1:N ProductAttribute
                </Tag>
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                  Chaque produit peut avoir ses propres critères (0, 1 ou plusieurs). Indépendants de la catégorie.
                </Text>
              </Space>
            }
            style={{ marginBottom: 16, border: '1px solid #bbf7d0', background: 'linear-gradient(180deg,#f0fdf4 0%,#ffffff 45%)' }}
          >
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 12 }}
              message="Ces critères sont propres à CE produit uniquement. Deux produits de la même famille n'ont pas forcément les mêmes critères."
              description={<>Exemple : Produit A = Puissance 3KVA + Phase Mono / Produit B = Tension 400V + Autonomie (même famille UPS).</>}
            />
            <Space direction="vertical" style={{ width: '100%' }} size={10}>
              {dynAttrRows.map((row, idx) => (
                <Row key={`dyn-attr-row-${idx}`} gutter={12} align="middle">
                  <Col xs={24} md={8}>
                    <Input
                      value={row.criterion}
                      onChange={e => updateDynAttrRow(idx, 'criterion', e.target.value)}
                      placeholder="Nom du critère (ex: Puissance)"
                      allowClear
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Input
                      value={row.value}
                      onChange={e => updateDynAttrRow(idx, 'value', e.target.value)}
                      placeholder="Valeur (ex: 3 KVA)"
                      allowClear
                    />
                  </Col>
                  <Col xs={24} md={4}>
                    <Button
                      danger
                      icon="❌"
                      onClick={() => {
                        if (dynAttrRows.length <= 1) {
                          setDynAttrRows([{ criterion: '', value: '' }]);
                        } else {
                          removeDynAttrRow(idx);
                        }
                      }}
                      style={{ width: '100%' }}
                    >
                      Supprimer
                    </Button>
                  </Col>
                </Row>
              ))}
              <Row>
                <Col xs={24}>
                  <Button
                    type="dashed"
                    icon="➕"
                    onClick={addDynAttrRow}
                    style={{ width: '100%', borderStyle: 'dashed', color: '#15803d' }}
                  >
                    Ajouter un critère
                  </Button>
                </Col>
              </Row>
            </Space>
            {dynAttrRows.filter(r => String(r.criterion || '').trim() !== '').length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <Tag color="green">
                    {dynAttrRows.filter(r => String(r.criterion || '').trim() !== '').length} critère(s) actif(s) sur ce produit
                  </Tag>
                </div>
              )
            }
          </Card>

          {/* 🔑 NOUVEAU : CRITÈRES EN CASCADE STRICTE — Catégorie → Critères → Valeurs */}
          <Card
            title={
              <Space>
                <span style={{ fontSize: 16 }}>🔑 Critères Produit (Cascade stricte)</span>
                <Tag color="geekblue" style={{ marginLeft: 4 }}>
                  Catégorie → Critères → Valeurs
                </Tag>
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                  Pas de mélange inter-catégories : changement de Catégorie ⇒ reset des Critères.
                </Text>
              </Space>
            }
            style={{ marginBottom: 16, border: '1px solid #c7d2fe', background: 'linear-gradient(180deg,#eef2ff 0%,#ffffff 45%)' }}
            loading={facFamilleLoading}
          >
            {facFamilyOptions.length === 0 ? (
              <Empty
                description={(
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ marginBottom: 6, fontSize: 14 }}>
                      Aucune famille de critères disponible pour le moment.
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      Importez d'abord des produits avec des colonnes <code>Critère N / Valeur Critère N</code>,
                      ou configurez <code>facets._familleCriteres</code>.
                    </div>
                  </div>
                )}
              />
            ) : (
              (() => {
                const currentBloc = getCurrentFamilleCriteres();
                const familleActuelle =
                  (currentBloc && currentBloc.famille) ||
                  (selectedRootName && facFamilyOptions.find(f => f.famille === selectedRootName) ? selectedRootName : '') ||
                  '';
                const familleData = facFamilyOptions.find(f => f.famille === familleActuelle) || null;
                const criteresDisposFam = familleData && Array.isArray(familleData.criteres)
                  ? familleData.criteres.filter(c => c && c.nom)
                  : [];
                return (
                  <>
                    <Row gutter={[16, 12]}>
                      {/* Étape 1 : Choisir la Catégorie (Famille) */}
                      <Col xs={24} sm={16} md={12} lg={8}>
                        <Form.Item
                          label={
                            <span>
                              <Tag color="#002c3e" style={{ color: '#fff', fontWeight: 700 }}>1</Tag>
                              &nbsp;
                              <strong>Catégorie (Famille)</strong>
                            </span>
                          }
                          required
                        >
                          <Select
                            showSearch
                            allowClear
                            size="large"
                            placeholder="Choisir une catégorie..."
                            optionFilterProp="label"
                            value={familleActuelle || undefined}
                            onChange={(fam) => {
                              if (!fam) {
                                setFamilleName('');
                                return;
                              }
                              setFamilleName(String(fam));
                            }}
                            style={{ width: '100%' }}
                            options={facFamilyOptions.map(f => ({
                              value: f.famille,
                              label: `${f.famille}  (${f.countProducts ?? 0} produits · ${f.countCriteres ?? 0} critères)`
                            }))}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    {/* Étape 2 + 3 : Critères (seulement ceux de la famille) → Valeurs */}
                    {familleActuelle && (
                      criteresDisposFam.length === 0 ? (
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginTop: 4 }}
                          message={`Aucun critère défini pour la catégorie "${familleActuelle}"`}
                          description={(
                            <span>
                              Ajoutez des colonnes <code>Critère N / Valeur Critère N</code> lors de l'import Excel,
                              ou renseignez directement <code>facets._familleCriteres</code>.
                            </span>
                          )}
                        />
                      ) : (
                        <Row gutter={[16, 12]} style={{ marginTop: 4 }}>
                          {criteresDisposFam.map((crit, i) => {
                            const vals = Array.isArray(crit.valeurs) ? crit.valeurs.filter(v => v != null && String(v).trim()) : [];
                            const sel = getFamCritSelection(crit.nom);
                            return (
                              <Col xs={24} sm={12} md={8} lg={6} key={`fam-crit-${crit.nom}-${i}`}>
                                <Form.Item
                                  label={(
                                    <span>
                                      <Tag color="#0284c7" style={{ color: '#fff', fontWeight: 700 }}>{i + 1}</Tag>
                                      &nbsp;
                                      <strong>{String(crit.nom)}</strong>
                                      <Text type="secondary" style={{ fontSize: 11, marginLeft: 6, fontWeight: 400 }}>
                                        ({vals.length} valeur{vals.length > 1 ? 's' : ''})
                                      </Text>
                                    </span>
                                  )}
                                >
                                  <Select
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    size="large"
                                    placeholder={`Valeur pour « ${String(crit.nom)} »...`}
                                    value={sel || undefined}
                                    onChange={(v) => setFamilleCritereValeur(familleActuelle, String(crit.nom), v || '')}
                                    style={{ width: '100%' }}
                                    options={[
                                      { value: '', label: `— Toutes les valeurs de "${String(crit.nom)}" —` },
                                      ...vals.map((v) => ({ value: v, label: v }))
                                    ]}
                                  />
                                </Form.Item>
                              </Col>
                            );
                          })}
                        </Row>
                      )
                    )}

                    {/* Synthèse des sélections */}
                    {familleActuelle && currentBloc && Array.isArray(currentBloc.items) && currentBloc.items.length > 0 && (
                      <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: '#f0fdf4', border: '1px dashed #22c55e', fontSize: 12, color: '#15803d' }}>
                        <strong style={{ color: '#15803d' }}>
                          ✅ {currentBloc.items.length} critère{currentBloc.items.length > 1 ? 's' : ''} renseigné{currentBloc.items.length > 1 ? 's' : ''}
                        </strong>
                        &nbsp;pour la catégorie <b>{familleActuelle}</b> :
                        <ul style={{ marginTop: 6, paddingLeft: 18, marginBottom: 0 }}>
                          {currentBloc.items.map((it, idx) => (
                            <li key={`sel-sum-${idx}`} style={{ marginBottom: 2 }}>
                              <b>{it.nom}</b> → <span style={{ color: '#166534' }}>{it.valeur}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()
            )}
          </Card>

          {/* 🎛️ Nouveau: Critères Dynamiques Branches */}
          <Card
            title={
              <span>
                🎛️ Critères dynamiques
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 8, fontWeight: 400 }}>
                  Configuration Admin → Catégories & Critères (Famille)
                </Text>
              </span>
            }
            style={{ marginBottom: 16 }}
            loading={dynLoading}
          >
            {dynBranches.length === 0 ? (
              <Empty
                description={
                  <span>
                    Aucun critère configuré. Sélectionnez d'abord une catégorie racine / famille principale
                    pour voir ses branches apparaître ici.
                  </span>
                }
              />
            ) : (
              <Row gutter={[16, 12]}>
                {dynBranches.map((branch) => {
                  if (!branch) return null;
                  const opts = flattenBranchOptions(branch);
                  const value = dynBranchSelections[branch.id] || undefined;
                  const tagColor = branch.color || null;
                  return (
                    <Col xs={24} sm={12} md={8} key={branch.id}>
                      <Form.Item
                        label={
                          <span>
                            <Tag color={tagColor} style={{ marginRight: 6, marginBottom: 4 }}>
                              {branch.type === 'criterion' ? '📁' : (branch.type === 'family' ? '🏠' : '🍃')}{' '}
                              {branch.name}
                            </Tag>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {opts.length === 0
                                ? ' (aucune valeur définie)'
                                : ` (${opts.length} valeur(s))`}
                            </Text>
                          </span>
                        }
                      >
                        <Select
                          allowClear
                          showSearch
                          placeholder={`Choisir une valeur pour ${branch.name}...`}
                          value={value}
                          onChange={(vid) => handleDynBranchChange(branch.id, vid)}
                          options={opts}
                          style={{ width: '100%' }}
                          size="large"
                          filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                          }
                        />
                      </Form.Item>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card>

          {/* 3) CHAMPS SPÉCIFIQUES FAMILLE */}
          {specificFields.length > 0 && (
            <>
              <Divider titlePlacement="left">⚙️ Champs spécifiques — {selectedRootName}</Divider>
              <Row gutter={16}>
                {specificFields.map(f => (
                  <Col xs={24} md={8} key={`sp-${f.key}`}>
                    <Form.Item label={f.label + (f.required ? ' *' : '')} required={f.required}>
                      {f.type === 'textarea' ? (
                        <TextArea rows={3}
                          value={formData.technicalSpecs?.[f.key] ?? ''}
                          onChange={e => setField(`technicalSpecs.${f.key}`, e.target.value)}
                          placeholder={f.label} />
                      ) : f.type === 'number' ? (
                        <InputNumber style={{ width: '100%' }}
                          value={formData.technicalSpecs?.[f.key]}
                          onChange={v => setField(`technicalSpecs.${f.key}`, v)}
                          placeholder={f.label} />
                      ) : (f.type === 'select' || f.type === 'multiselect') ? (
                        <Select mode={f.isMultiple ? 'multiple' : undefined} allowClear showSearch
                          value={formData.technicalSpecs?.[f.key]}
                          onChange={v => setField(`technicalSpecs.${f.key}`, v)}
                          options={f.options.map(opt => ({ value: opt, label: opt }))}
                          placeholder={f.label}
                          filterOption={(inp, opt) => (opt.label || '').toString().toLowerCase().includes(inp.toLowerCase())} />
                      ) : (
                        <Input value={formData.technicalSpecs?.[f.key] ?? ''}
                          onChange={e => setField(`technicalSpecs.${f.key}`, e.target.value)}
                          placeholder={f.label} />
                      )}
                    </Form.Item>
                  </Col>
                ))}
              </Row>
            </>
          )}

          {/* Champs spécifiques feuille */}
          {effectiveCategory && (() => {
            const leafFields = getCategorySpecificFieldsById(Number(effectiveCategory.id));
            if (leafFields.length === 0) return null;
            return (
              <>
                <Divider titlePlacement="left">🔧 Champs spécifiques — {effectiveCategory.name}</Divider>
                <Row gutter={16}>
                  {leafFields.map(f => (
                    <Col xs={24} md={8} key={`sp-leaf-${f.key}`}>
                      <Form.Item label={f.label + (f.required ? ' *' : '')} required={f.required}>
                        {f.type === 'textarea' ? (
                          <TextArea rows={3} value={formData.technicalSpecs?.[f.key] ?? ''} onChange={e => setField(`technicalSpecs.${f.key}`, e.target.value)} />
                        ) : f.type === 'number' ? (
                          <InputNumber style={{ width: '100%' }} value={formData.technicalSpecs?.[f.key]} onChange={v => setField(`technicalSpecs.${f.key}`, v)} />
                        ) : (f.type === 'select' || f.type === 'multiselect') ? (
                          <Select mode={f.isMultiple ? 'multiple' : undefined} allowClear showSearch
                            value={formData.technicalSpecs?.[f.key]}
                            onChange={v => setField(`technicalSpecs.${f.key}`, v)}
                            options={f.options.map(opt => ({ value: opt, label: opt }))} />
                        ) : (
                          <Input value={formData.technicalSpecs?.[f.key] ?? ''} onChange={e => setField(`technicalSpecs.${f.key}`, e.target.value)} />
                        )}
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              </>
            );
          })()}

          {/* 6) CARACTÉRISTIQUES */}
          <Divider titlePlacement="left">• Caractéristiques (liste à puces)</Divider>
          <Form.Item label="Une ligne par caractéristique">
            <TextArea rows={4}
              value={getArrayFieldAsText('features')}
              onChange={e => handleArrayInputChange('features', e.target.value)}
              placeholder={`Ex:\n- IP65, résistant aux intempéries\n- Autonomie 3h en mode évacuation\n- Norme NF EN 50171`}
            />
          </Form.Item>

          {/* 7) IMAGES */}
          <Divider titlePlacement="left">🖼️ Images du produit</Divider>
          <Row gutter={[12, 12]}>
            {images.map((img, i) => {
              const src = img.url || (typeof img.file?.preview === 'string' ? img.file.preview : '');
              return (
                <Col xs={24} md={6} key={`img-${i}`}>
                  <Card size="small"
                    style={{ border: img.isMain ? '2px solid #1677ff' : '1px solid #d9d9d9' }}
                    cover={src ? (
                      <img alt={`img-${i}`} src={src} style={{ height: 140, objectFit: 'cover' }} />
                    ) : (
                      <div style={{ height: 140, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                        {img.file?.name || 'Image'}
                      </div>
                    )}
                  >
                    <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                      <Button size="small" type={img.isMain ? 'primary' : 'default'} onClick={() => setMainImage(i)} block>
                        {img.isMain ? '⭐ Principale' : 'Définir principale'}
                      </Button>
                      <Button size="small" danger onClick={() => removeImage(i)} block>🗑️ Supprimer</Button>
                    </Space>
                  </Card>
                </Col>
              );
            })}
            <Col xs={24} md={6}>
              <Card size="small" style={{ height: '100%', minHeight: 240, border: '1px dashed #d9d9d9' }}>
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
                  <Text type="secondary">Glissez ou cliquez</Text>
                  <Upload multiple beforeUpload={() => false} showUploadList={false} fileList={[]}
                    onChange={info => {
                      // info.fileList contient TOUS les fichiers de la session (Ant conserve cumulatif)
                      // mais nous on veut SEULEMENT les NOUVEAUX : comparer originFileObj to existant
                      const existingUids = new Set(
                        images.map(x => (x.file && typeof x.file === 'object' && x.file.uid != null) ? String(x.file.uid) : '')
                          .filter(Boolean)
                      );
                      const nouveaus = [];
                      info.fileList?.forEach(f => {
                        if (!f.originFileObj && !f.url) return;
                        const id = f.originFileObj?.uid;
                        if (id != null && existingUids.has(String(id))) return; // déjà dans la liste
                        nouveaus.push({
                          file: f.originFileObj || null,
                          url: f.url || (typeof f.thumbUrl === 'string' ? f.thumbUrl : null),
                          isExisting: !!f.url && !f.originFileObj,
                          isMain: false
                        });
                      });
                      if (nouveaus.length > 0) setImages(prev => [...prev, ...nouveaus]);
                    }}
                  >
                    <Button type="primary">+ Ajouter images</Button>
                  </Upload>
                </div>
              </Card>
            </Col>
          </Row>

          {/* 8) DOCUMENTS */}
          <Divider titlePlacement="left">📄 Documents</Divider>
          <Row gutter={16}>
            {[
              { k: 'datasheet', lab: '📊 Fiche technique (PDF)' },
              { k: 'manual', lab: '📖 Manuel utilisateur' },
              { k: 'certificate', lab: '🏅 Certificat / attestation' }
            ].map(doc => (
              <Col xs={24} md={8} key={`doc-${doc.k}`}>
                <Form.Item label={doc.lab}>
                  {documents[doc.k]?.url ? (
                    <Space orientation="vertical" style={{ width: '100%' }}>
                      <Tag color="blue" style={{ padding: '4px 12px' }}>✅ {String(documents[doc.k].url.split('/').pop()).slice(0, 40)}</Tag>
                      <Upload beforeUpload={() => false} showUploadList={false} fileList={[]}
                        onChange={info => { const f = info.file; if (f.originFileObj) handleDocumentUpload(doc.k, f.originFileObj); }}>
                        <Button size="small">🔄 Remplacer</Button>
                      </Upload>
                    </Space>
                  ) : documents[doc.k]?.file ? (
                    <Space orientation="vertical" style={{ width: '100%' }}>
                      <Tag color="green" style={{ padding: '4px 12px' }}>⏳ {String(documents[doc.k].file.name).slice(0, 40)}</Tag>
                      <Button size="small" danger onClick={() => setDocuments(prev => ({ ...prev, [doc.k]: null }))}>Annuler</Button>
                    </Space>
                  ) : (
                    <Upload beforeUpload={() => false} showUploadList={false} fileList={[]}
                      onChange={info => { const f = info.file; if (f.originFileObj) handleDocumentUpload(doc.k, f.originFileObj); }}>
                      <Button>Téléverser {String(doc.lab.split('(')[0])}</Button>
                    </Upload>
                  )}
                </Form.Item>
              </Col>
            ))}
          </Row>

          {/* 9) SEO */}
          <Divider titlePlacement="left">🔎 SEO</Divider>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Meta Title">
                <Input value={formData.metaTitle} onChange={e => setField('metaTitle', e.target.value)} placeholder="Titre SEO, < 60 caractères" maxLength={70} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Mots-clés SEO (virgules)">
                <Select mode="tags" style={{ width: '100%' }} open={false}
                  value={formData.seoKeywords || []}
                  onChange={v => setField('seoKeywords', v)}
                  placeholder="Ex: éclairage sécurité, bloc évacuation, NF EN 50171" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="Meta Description">
                <TextArea rows={3} maxLength={320} value={formData.metaDescription}
                  onChange={e => setField('metaDescription', e.target.value)}
                  placeholder="Description SEO, < 160 caractères" />
              </Form.Item>
            </Col>
          </Row>

          {/* 10) ACTIONS */}
          <Divider />
          <Space wrap size="middle">
            <Button type="primary" size="large" htmlType="submit" loading={isLoading}>
              💾 {isEdit ? 'Enregistrer les modifications' : 'Créer le produit (Brouillon)'}
            </Button>
            <Button size="large" loading={isLoading} onClick={e => handleSubmit(e, 'published')}>✅ Publier immédiatement</Button>
            <Button size="large" onClick={() => navigate('/admin/products')}>↩️ Annuler</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default ProductForm;
